import { useEffect, useRef, useState } from 'react'

/* Dim Cricket League "(e)" ball-by-ball board — reference admin
 * /admin/tpVirtual/… (`.banner > .scorecard-bb > .bb-match`).
 *
 * Ported from the player app's BallByBallScoreboard
 * (frontend/src/pages/VirtualCricketDetails.jsx): the provider's score page
 * (`score` url from /user/virtual/event-media) runs in a HIDDEN iframe purely
 * as the data feed and postMessages `{type:'scorecard_update', data}`; the
 * board is our own markup. Empty until the first update lands, so the
 * provider's "Waiting for live data..." placeholder never shows.
 */

// A plain "W" is a wicket too (red dot like the reference, user 2026-10-24)
function ballDotClass(b) {
  const s = String(b).toUpperCase()
  if (s === 'W' || s === 'WW' || /LBW|CT|BO|RO|ST|HW/.test(s)) return 'dot wicket'
  if (s.includes('WD') || s.includes('WIDE')) return 'dot wide'
  if (s.includes('NB') || s.includes('NOBALL')) return 'dot noball'
  if (s === '4') return 'dot four'
  if (s === '6') return 'dot six'
  return 'dot'
}
function ballDotText(b) {
  const s = String(b).toUpperCase()
  if (s === 'W' || s === 'WW' || /LBW|CT|BO|RO|ST|HW/.test(s)) return 'w'
  if (s.includes('WD') || s.includes('WIDE')) return 'wd'
  if (s.includes('NB') || s.includes('NOBALL')) return 'nb'
  return String(b)
}

function BbTeam({ name, score, active, balls }) {
  return (
    <div className="bb-team">
      <div className="bb-score">
        <span className={active ? 'bb-current' : ''}>{score || 'Yet to bat'}</span>
      </div>{' '}
      <div className="bb-team-name">{name}</div>{' '}
      <div className="bb-team-form">
        {(balls || []).map((b, i) => (
          <span key={i} className={ballDotClass(b)}>{ballDotText(b)}</span>
        ))}
      </div>
    </div>
  )
}

// The provider's score.html sends a NESTED summary; an older host sends a FLAT
// one (spnnation1, score1, activenation1, one `balls` array). Normalised here.
function normalize(d) {
  if (d?.teams) {
    const t1 = d.teams.team1 || {}
    const t2 = d.teams.team2 || {}
    return {
      name1: t1.name, score1: t1.score, balls1: Array.isArray(t1.balls) ? t1.balls : [],
      name2: t2.name, score2: t2.score, balls2: Array.isArray(t2.balls) ? t2.balls : [],
      active1: d.inPlay === 'team1', active2: d.inPlay === 'team2',
      status: d.matchStatus || 'Live', // CSS uppercases it ("STARTED")
      mini: d.commentaryMessage || '',
      nested: true,
    }
  }
  return {
    name1: d?.spnnation1, score1: d?.score1,
    name2: d?.spnnation2, score2: d?.score2,
    active1: d?.activenation1 === '1', active2: d?.activenation2 === '1',
    status: 'Started',
    mini: d?.spnballrunningstatus || d?.spnmessage || '',
    nested: false,
  }
}

export default function DimScoreboard({ scoreUrl }) {
  const [score, setScore] = useState(null)
  // flat feed only carries the CURRENT innings' balls — keep each side's last
  const ballsRef = useRef({ 1: [], 2: [] })

  useEffect(() => {
    ballsRef.current = { 1: [], 2: [] }
    setScore(null)
    if (!scoreUrl) return undefined
    let origin
    try { origin = new URL(scoreUrl).origin } catch { return undefined }
    const onMessage = (e) => {
      if (e.origin !== origin || e.data?.type !== 'scorecard_update' || !e.data.data) return
      const d = e.data.data
      if (!d.teams) {
        const balls = Array.isArray(d.balls) ? d.balls : []
        if (d.activenation1 === '1') ballsRef.current[1] = balls
        else if (d.activenation2 === '1') ballsRef.current[2] = balls
      }
      setScore(d)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [scoreUrl])

  const v = score ? normalize(score) : null
  return (
    <div className="scorecard-bb">
      {scoreUrl && <iframe src={scoreUrl} title="Scorecard feed" style={{ display: 'none' }}></iframe>}
      {v && (
        <>
          <div className="bb-match">
            <BbTeam name={v.name1 || 'Team 1'} score={v.score1} active={v.active1} balls={v.nested ? v.balls1 : ballsRef.current[1]} />{' '}
            <div className="bb-scorebox">
              {/* reference admin: plain `bb-inning`, no `live` modifier */}
              <div className="bb-inning"><span>{v.status}</span></div>{' '}
              {v.mini && <div className="bb-mini d-none-mobile">{v.mini}</div>}
            </div>{' '}
            <BbTeam name={v.name2 || 'Team 2'} score={v.score2} active={v.active2} balls={v.nested ? v.balls2 : ballsRef.current[2]} />
          </div>{' '}
          {v.mini && <div className="bb-mini d-none-desktop">{v.mini}</div>}
        </>
      )}
    </div>
  )
}
